import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, MessageCircle, Trash2, Check, X, UserMinus, Copy, RefreshCw, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, Loader } from "@/components/cast/Common";
import { Emblem } from "@/components/cast/Emblem";
import { ReportDialog } from "@/components/cast/ReportDialog";

function Thread({ t, canModerate, onDeleted }: any) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState<any[]>([]);
  const [text, setText] = useState("");
  const toggle = async () => { if (!open) setReplies((await api.get(`/threads/${t.id}/replies`)).data); setOpen(!open); };
  const reply = async () => { try { const { data } = await api.post(`/threads/${t.id}/replies`, { text }); setReplies([...replies, data]); setText(""); } catch (e) { toast.error(errMsg(e)); } };
  const delReply = async (id: string) => { await api.delete(`/replies/${id}`); setReplies(replies.filter((r) => r.id !== id)); };
  const mine = t.author_id === user.id;
  return (
    <div className="card-warm p-6" data-testid={`thread-${t.id}`}>
      <div className="flex items-start gap-3">
        <Emblem avatar={t.author.avatar} size={36} />
        <div className="flex-1">
          <h4 className="font-display text-xl font-semibold text-slate-900">{t.title}</h4>
          <p className="text-xs text-slate-500">{t.author.username} · {timeAgo(t.created_at)}</p>
          <p className="mt-3 whitespace-pre-wrap text-slate-700">{t.body}</p>
        </div>
        {(mine || canModerate) && <Button variant="ghost" size="icon" data-testid={`thread-delete-${t.id}`} onClick={async () => { await api.delete(`/threads/${t.id}`); onDeleted(t.id); }}><Trash2 className="h-4 w-4" /></Button>}
      </div>
      <div className="mt-4 flex items-center gap-4">
        <button data-testid={`thread-replies-toggle-${t.id}`} onClick={toggle} className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900"><MessageCircle className="h-4 w-4" />{open ? "Hide replies" : `${t.reply_count} replies`}</button>
        {!mine && <ReportDialog small targetType="thread" targetId={t.id} testId={`report-thread-${t.id}`} />}
      </div>
      {open && (
        <div className="mt-4 space-y-3 border-t border-[#E2DBCE] pt-4">
          {replies.map((r) => (
            <div key={r.id} className="flex gap-3" data-testid={`reply-${r.id}`}>
              <Emblem avatar={r.author.avatar} size={28} />
              <div className="flex-1 rounded-xl bg-[#FAF7F2] px-4 py-2"><p className="text-sm font-semibold">{r.author.username}</p><p className="text-sm text-slate-700">{r.text}</p></div>
              {(r.author_id === user.id || canModerate) && <button data-testid={`reply-delete-${r.id}`} onClick={() => delReply(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
          <div className="flex gap-2"><Input data-testid={`reply-input-${t.id}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply…" className="bg-white" /><Button data-testid={`reply-submit-${t.id}`} disabled={!text.trim()} onClick={reply} className="rounded-full bg-[#1E293B]">Reply</Button></div>
        </div>
      )}
    </div>
  );
}

function InviteLink({ g }: { g: any }) {
  const [inv, setInv] = useState<any>(g.invite || null);
  const link = inv ? `${window.location.origin}/join/${inv.token}` : "";
  const create = async () => { try { setInv((await api.post(`/groups/${g.id}/invite`)).data); toast.success("New invite link ready"); } catch (e) { toast.error(errMsg(e)); } };
  const revoke = async () => { await api.delete(`/groups/${g.id}/invite`); setInv(null); toast.success("Invite link turned off"); };
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch { toast.error("Couldn't copy. Select the link and copy it."); } };
  return (
    <div data-testid="invite-link-panel">
      <p className="font-semibold text-slate-800">Invite link</p>
      <p className="mt-1 text-sm text-slate-500">Anyone with the link joins right away, no approval needed. Links last 7 days.</p>
      {inv ? (
        <div className="mt-3 space-y-2">
          <Input readOnly value={link} data-testid="invite-link-input" onFocus={(e) => e.target.select()} className="bg-[#FAF7F2] text-xs" />
          <p className="text-xs text-slate-500" data-testid="invite-link-expiry">Expires {new Date(inv.expires_at).toLocaleString()}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" data-testid="invite-copy-button" onClick={copy} className="rounded-full bg-[#1E293B]"><Copy className="mr-1 h-3.5 w-3.5" />Copy</Button>
            <Button size="sm" variant="outline" data-testid="invite-reset-button" onClick={create} className="rounded-full"><RefreshCw className="mr-1 h-3.5 w-3.5" />New link</Button>
            <Button size="sm" variant="ghost" data-testid="invite-revoke-button" onClick={revoke} className="rounded-full text-red-600">Turn off</Button>
          </div>
        </div>
      ) : <Button size="sm" data-testid="invite-create-button" onClick={create} className="mt-3 rounded-full bg-[#C86D51] hover:bg-[#B35A3F]"><Link2 className="mr-1 h-3.5 w-3.5" />Create invite link</Button>}
    </div>
  );
}

function LeaderPanel({ g, reload }: { g: any; reload: () => void }) {
  const [mods, setMods] = useState<any[]>([]);
  useEffect(() => { api.get("/modules").then((r) => setMods(r.data)); }, []);
  const decide = async (uid: string, action: string) => { await api.post(`/groups/${g.id}/requests/${uid}`, { action }); reload(); };
  const remove = async (uid: string) => { await api.post(`/groups/${g.id}/members/${uid}/remove`); reload(); };
  const setStudy = async (v: string) => { await api.put(`/groups/${g.id}/study`, { module_id: v === "none" ? null : v }); reload(); toast.success("Group study updated"); };
  return (
    <div className="card-warm space-y-6 p-6" data-testid="leader-panel">
      <p className="eyebrow">Leader tools</p>
      <InviteLink g={g} />
      <div>
        <p className="font-semibold text-slate-800">Join requests ({g.pending.length})</p>
        {g.pending.length === 0 ? <p className="mt-1 text-sm text-slate-500">No pending requests.</p> : g.pending.map((p: any) => (
          <div key={p.id} className="mt-3 flex items-center gap-2" data-testid={`pending-${p.username}`}>
            <Emblem avatar={p.avatar} size={28} /><span className="flex-1 text-sm font-semibold">{p.username}</span>
            <Button size="icon" variant="outline" data-testid={`approve-${p.username}`} onClick={() => decide(p.id, "approve")}><Check className="h-4 w-4 text-emerald-600" /></Button>
            <Button size="icon" variant="outline" data-testid={`reject-${p.username}`} onClick={() => decide(p.id, "reject")}><X className="h-4 w-4 text-red-600" /></Button>
          </div>
        ))}
      </div>
      <div>
        <p className="mb-2 font-semibold text-slate-800">Shared study track</p>
        <Select value={g.module_id || "none"} onValueChange={setStudy}><SelectTrigger data-testid="leader-study-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{mods.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent></Select>
      </div>
      <div>
        <p className="font-semibold text-slate-800">Members</p>
        {g.members.filter((m: any) => m.id !== g.leader_id).map((m: any) => (
          <div key={m.id} className="mt-2 flex items-center gap-2"><Emblem avatar={m.avatar} size={26} /><span className="flex-1 text-sm">{m.username}</span>
            <Button size="icon" variant="ghost" data-testid={`remove-member-${m.username}`} onClick={() => remove(m.id)}><UserMinus className="h-4 w-4" /></Button></div>
        ))}
      </div>
    </div>
  );
}

export default function Group() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [g, setG] = useState<any>(null);
  const [threads, setThreads] = useState<any[]>([]);
  const [f, setF] = useState({ title: "", body: "" });
  const load = () => api.get(`/groups/${groupId}`).then((r) => { setG(r.data); if (["member", "leader"].includes(r.data.my_status)) api.get(`/groups/${groupId}/threads`).then((t) => setThreads(t.data)); });
  useEffect(() => { load(); }, [groupId]); // eslint-disable-line
  if (!g) return <Loader />;
  const isMember = ["member", "leader"].includes(g.my_status);
  const canModerate = g.my_status === "leader" || user.role === "admin";
  const post = async () => { try { const { data } = await api.post(`/groups/${g.id}/threads`, f); setThreads([data, ...threads]); setF({ title: "", body: "" }); } catch (e) { toast.error(errMsg(e)); } };
  const leave = async () => { await api.post(`/groups/${g.id}/leave`); nav("/groups"); };

  return (
    <div className="space-y-8">
      <Link to="/groups" data-testid="group-back-link" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600"><ChevronLeft className="h-4 w-4" />All groups</Link>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="eyebrow">Groups · {g.focus === "support" ? "Support group" : "Bible study"}</p><h1 className="mt-3 font-display text-4xl font-semibold text-slate-900 sm:text-5xl" data-testid="group-title">{g.name}</h1><p className="mt-3 max-w-2xl text-slate-600">{g.description}</p></div>
        {g.my_status === "member" && <Button variant="outline" data-testid="group-leave-button" onClick={leave} className="rounded-full">Leave group</Button>}
      </div>
      {!isMember ? (
        <div className="space-y-4" data-testid="group-locked">
          <Empty title="This group is private" body={g.my_status === "pending" ? "Your request is waiting for the leader's approval." : "Request to join and the leader will review your request."} />
          {g.my_status === "none" ? <Button data-testid="group-locked-join-button" onClick={async () => { await api.post(`/groups/${g.id}/join`); toast.success("Request sent to the group leader"); load(); }} className="rounded-full bg-[#1E293B]">Request to join</Button>
            : <Button disabled variant="outline" className="rounded-full" data-testid="group-locked-pending">Request pending</Button>}
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-8">
            <div className="card-warm space-y-3 p-6">
              <Input data-testid="thread-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Start a discussion" className="bg-white" />
              <Textarea data-testid="thread-body-input" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Share a question, reflection, or prayer focus…" className="bg-white" />
              <Button data-testid="thread-submit-button" disabled={f.title.trim().length < 3 || !f.body.trim()} onClick={post} className="rounded-full bg-[#C86D51] hover:bg-[#B35A3F]">Post discussion</Button>
            </div>
            {threads.length === 0 ? <Empty title="No discussions yet" /> : threads.map((t) => <Thread key={t.id} t={t} canModerate={canModerate} onDeleted={(id: string) => setThreads(threads.filter((x) => x.id !== id))} />)}
          </div>
          <aside className="space-y-6 lg:col-span-4">
            {g.module ? (
              <Link to={`/deep-water/${g.module.id}`} data-testid="group-study-link" className="block overflow-hidden rounded-2xl border border-[#E2DBCE] bg-white transition-shadow hover:shadow-md">
                <img src={g.module.image} alt="" className="aspect-[16/9] w-full object-cover" />
                <div className="p-5"><p className="eyebrow">Group study</p><p className="mt-1 font-display text-2xl font-semibold">{g.module.title}</p><p className="text-sm text-slate-600">{g.module.summary}</p></div>
              </Link>
            ) : <div className="card-warm p-5 text-sm text-slate-500">No shared study track yet.</div>}
            {canModerate && <LeaderPanel g={g} reload={load} />}
            {!canModerate && <div className="card-warm p-5"><p className="font-semibold">{g.member_count} members</p><div className="mt-3 flex flex-wrap gap-2">{g.members.map((m: any) => <span key={m.id} title={m.username}><Emblem avatar={m.avatar} size={30} /></span>)}</div></div>}
          </aside>
        </div>
      )}
    </div>
  );
}
